import type { ReactNode } from "react";
import i18n from "@/utils/i18n";

export type ReportCategoryKey =
  | "financial"
  | "strategic"
  | "operational"
  | "quality"
  | "compliance"
  | "engagement"
  | "demographics";

export type ReportKey =
  // Existing
  | "overview"
  | "education-performance"
  // New 10
  | "budget"
  | "department-investment"
  | "trainer-performance"
  | "learning-paths"
  | "certificate-pipeline"
  | "certificate-hierarchy"
  | "intern-profile"
  | "virtual-classroom"
  | "survey-nps"
  | "exam-analytics"
  | "compliance"
  // Genişletme — 12 yeni
  | "login-activity"
  | "library-consumption"
  | "communication-effectiveness"
  | "support-sla"
  | "personnel-demographics"
  | "location-performance"
  | "attendance-tracking"
  | "exam-difficulty"
  | "certificate-validity"
  | "roi-investment"
  | "enrollment-funnel"
  | "survey-cross-tab";

export interface ReportCatalogEntry {
  key: ReportKey;
  category: ReportCategoryKey;
  title: string;
  description: string;
  route: string;
  iconName: string;
  /** Backend ReportTypeEnum eşleği — export ve registry için */
  reportType: string;
  /** Mevcut raporlar hub'dan tab olarak açılır */
  legacyTabQuery?: "overview" | "education";
  isImplemented?: boolean;
}

export interface ReportCategoryMeta {
  key: ReportCategoryKey;
  title: string;
  description: string;
  order: number;
}

const t = (k: string) => i18n.t(k);

export const REPORT_CATEGORIES: ReportCategoryMeta[] = [
  {
    key: "financial",
    title: t("reportCatalog.categories.financial.title"),
    description: t("reportCatalog.categories.financial.description"),
    order: 1,
  },
  {
    key: "strategic",
    title: t("reportCatalog.categories.strategic.title"),
    description: t("reportCatalog.categories.strategic.description"),
    order: 2,
  },
  {
    key: "operational",
    title: t("reportCatalog.categories.operational.title"),
    description: t("reportCatalog.categories.operational.description"),
    order: 3,
  },
  {
    key: "quality",
    title: t("reportCatalog.categories.quality.title"),
    description: t("reportCatalog.categories.quality.description"),
    order: 4,
  },
  {
    key: "compliance",
    title: t("reportCatalog.categories.compliance.title"),
    description: t("reportCatalog.categories.compliance.description"),
    order: 5,
  },
  {
    key: "engagement",
    title: t("reportCatalog.categories.engagement.title"),
    description: t("reportCatalog.categories.engagement.description"),
    order: 6,
  },
  {
    key: "demographics",
    title: t("reportCatalog.categories.demographics.title"),
    description: t("reportCatalog.categories.demographics.description"),
    order: 7,
  },
];

export const REPORT_CATALOG: ReportCatalogEntry[] = [
  // Financial
  {
    key: "budget",
    category: "financial",
    title: t("reportCatalog.entries.budget.title"),
    description: t("reportCatalog.entries.budget.description"),
    route: "/dashboard/reports/budget",
    iconName: "finance",
    reportType: "BUDGET_VARIANCE",
    isImplemented: true,
  },
  {
    key: "department-investment",
    category: "financial",
    title: t("reportCatalog.entries.departmentInvestment.title"),
    description: t("reportCatalog.entries.departmentInvestment.description"),
    route: "/dashboard/reports/department-investment",
    iconName: "department",
    reportType: "DEPARTMENT_INVESTMENT",
    isImplemented: true,
  },
  // Strategic
  {
    key: "overview",
    category: "strategic",
    title: t("reportCatalog.entries.overview.title"),
    description: t("reportCatalog.entries.overview.description"),
    route: "/dashboard/reports/overview",
    iconName: "dashboard",
    reportType: "OVERVIEW",
    legacyTabQuery: "overview",
    isImplemented: true,
  },
  {
    key: "trainer-performance",
    category: "strategic",
    title: t("reportCatalog.entries.trainerPerformance.title"),
    description: t("reportCatalog.entries.trainerPerformance.description"),
    route: "/dashboard/reports/trainer-performance",
    iconName: "employeeManagement",
    reportType: "INSTRUCTOR_PERFORMANCE",
    isImplemented: true,
  },
  {
    key: "learning-paths",
    category: "strategic",
    title: t("reportCatalog.entries.learningPaths.title"),
    description: t("reportCatalog.entries.learningPaths.description"),
    route: "/dashboard/reports/learning-paths",
    iconName: "projects",
    reportType: "LEARNING_PATHS",
    isImplemented: true,
  },
  // Operational
  {
    key: "education-performance",
    category: "operational",
    title: t("reportCatalog.entries.educationPerformance.title"),
    description: t("reportCatalog.entries.educationPerformance.description"),
    route: "/dashboard/reports/education-performance",
    iconName: "assetCategory",
    reportType: "EDUCATION_PERFORMANCE",
    legacyTabQuery: "education",
    isImplemented: true,
  },
  {
    key: "certificate-pipeline",
    category: "operational",
    title: t("reportCatalog.entries.certificatePipeline.title"),
    description: t("reportCatalog.entries.certificatePipeline.description"),
    route: "/dashboard/reports/certificate-pipeline",
    iconName: "certificate",
    reportType: "CERTIFICATE_PIPELINE",
    isImplemented: true,
  },
  {
    key: "certificate-hierarchy",
    category: "operational",
    title: t("reportCatalog.entries.certificateHierarchy.title"),
    description: t("reportCatalog.entries.certificateHierarchy.description"),
    route: "/dashboard/reports/certificate-hierarchy",
    iconName: "reporting",
    reportType: "CERTIFICATE_HIERARCHY",
    isImplemented: true,
  },
  {
    key: "intern-profile",
    category: "operational",
    title: t("reportCatalog.entries.internProfile.title"),
    description: t("reportCatalog.entries.internProfile.description"),
    route: "/dashboard/reports/intern-profile",
    iconName: "employees",
    reportType: "INTERN_PLACEMENT",
    isImplemented: true,
  },
  {
    key: "virtual-classroom",
    category: "operational",
    title: t("reportCatalog.entries.virtualClassroom.title"),
    description: t("reportCatalog.entries.virtualClassroom.description"),
    route: "/dashboard/reports/virtual-classroom",
    iconName: "calendar",
    reportType: "VIRTUAL_CLASSROOM",
    isImplemented: true,
  },
  // Quality
  {
    key: "survey-nps",
    category: "quality",
    title: t("reportCatalog.entries.surveyNps.title"),
    description: t("reportCatalog.entries.surveyNps.description"),
    route: "/dashboard/reports/survey-nps",
    iconName: "heart",
    reportType: "SURVEY_SATISFACTION",
    isImplemented: true,
  },
  {
    key: "exam-analytics",
    category: "quality",
    title: t("reportCatalog.entries.examAnalytics.title"),
    description: t("reportCatalog.entries.examAnalytics.description"),
    route: "/dashboard/reports/exam-analytics",
    iconName: "question",
    reportType: "EXAM_ITEM_ANALYSIS",
    isImplemented: true,
  },
  // Compliance
  {
    key: "compliance",
    category: "compliance",
    title: t("reportCatalog.entries.compliance.title"),
    description: t("reportCatalog.entries.compliance.description"),
    route: "/dashboard/reports/compliance",
    iconName: "shield",
    reportType: "MANDATORY_COMPLIANCE",
    isImplemented: true,
  },
  // Engagement (yeni kategori)
  {
    key: "login-activity",
    category: "engagement",
    title: t("reportCatalog.entries.loginActivity.title"),
    description: t("reportCatalog.entries.loginActivity.description"),
    route: "/dashboard/reports/login-activity",
    iconName: "user",
    reportType: "LOGIN_ACTIVITY",
    isImplemented: true,
  },
  {
    key: "library-consumption",
    category: "engagement",
    title: t("reportCatalog.entries.libraryConsumption.title"),
    description: t("reportCatalog.entries.libraryConsumption.description"),
    route: "/dashboard/reports/library-consumption",
    iconName: "book",
    reportType: "LIBRARY_CONSUMPTION",
    isImplemented: true,
  },
  {
    key: "communication-effectiveness",
    category: "engagement",
    title: t("reportCatalog.entries.communicationEffectiveness.title"),
    description: t("reportCatalog.entries.communicationEffectiveness.description"),
    route: "/dashboard/reports/communication-effectiveness",
    iconName: "mail",
    reportType: "COMMUNICATION_EFFECTIVENESS",
    isImplemented: true,
  },
  {
    key: "support-sla",
    category: "engagement",
    title: t("reportCatalog.entries.supportSla.title"),
    description: t("reportCatalog.entries.supportSla.description"),
    route: "/dashboard/reports/support-sla",
    iconName: "support",
    reportType: "SUPPORT_SLA",
    isImplemented: true,
  },
  // Demographics (yeni kategori)
  {
    key: "personnel-demographics",
    category: "demographics",
    title: t("reportCatalog.entries.personnelDemographics.title"),
    description: t("reportCatalog.entries.personnelDemographics.description"),
    route: "/dashboard/reports/personnel-demographics",
    iconName: "employees",
    reportType: "PERSONNEL_DEMOGRAPHICS",
    isImplemented: true,
  },
  {
    key: "location-performance",
    category: "demographics",
    title: t("reportCatalog.entries.locationPerformance.title"),
    description: t("reportCatalog.entries.locationPerformance.description"),
    route: "/dashboard/reports/location-performance",
    iconName: "location",
    reportType: "LOCATION_PERFORMANCE",
    isImplemented: true,
  },
  // Operational (genişletme)
  {
    key: "attendance-tracking",
    category: "operational",
    title: t("reportCatalog.entries.attendanceTracking.title"),
    description: t("reportCatalog.entries.attendanceTracking.description"),
    route: "/dashboard/reports/attendance-tracking",
    iconName: "calendar",
    reportType: "ATTENDANCE_TRACKING",
    isImplemented: true,
  },
  {
    key: "exam-difficulty",
    category: "operational",
    title: t("reportCatalog.entries.examDifficulty.title"),
    description: t("reportCatalog.entries.examDifficulty.description"),
    route: "/dashboard/reports/exam-difficulty",
    iconName: "question",
    reportType: "EXAM_DIFFICULTY",
    isImplemented: true,
  },
  {
    key: "certificate-validity",
    category: "operational",
    title: t("reportCatalog.entries.certificateValidity.title"),
    description: t("reportCatalog.entries.certificateValidity.description"),
    route: "/dashboard/reports/certificate-validity",
    iconName: "certificate",
    reportType: "CERTIFICATE_VALIDITY",
    isImplemented: true,
  },
  // Strategic (genişletme)
  {
    key: "roi-investment",
    category: "strategic",
    title: t("reportCatalog.entries.roiInvestment.title"),
    description: t("reportCatalog.entries.roiInvestment.description"),
    route: "/dashboard/reports/roi-investment",
    iconName: "finance",
    reportType: "ROI_INVESTMENT",
    isImplemented: true,
  },
  {
    key: "enrollment-funnel",
    category: "strategic",
    title: t("reportCatalog.entries.enrollmentFunnel.title"),
    description: t("reportCatalog.entries.enrollmentFunnel.description"),
    route: "/dashboard/reports/enrollment-funnel",
    iconName: "projects",
    reportType: "ENROLLMENT_FUNNEL",
    isImplemented: true,
  },
  // Quality (genişletme)
  {
    key: "survey-cross-tab",
    category: "quality",
    title: t("reportCatalog.entries.surveyCrossTab.title"),
    description: t("reportCatalog.entries.surveyCrossTab.description"),
    route: "/dashboard/reports/survey-cross-tab",
    iconName: "heart",
    reportType: "SURVEY_CROSS_TAB",
    isImplemented: true,
  },
];

export function getReportsByCategory(
  category: ReportCategoryKey,
): ReportCatalogEntry[] {
  return REPORT_CATALOG.filter((r) => r.category === category);
}

export function getReportByKey(key: ReportKey): ReportCatalogEntry | undefined {
  return REPORT_CATALOG.find((r) => r.key === key);
}

// Helper type — React bağımlılığı yalnızca type olarak
export type _ReactNode = ReactNode;
